import type { Badge, UseAwardWalletBadgeFetchListParams } from '~/types/Award/badge';

import { BadgeTier } from '~/graphql/generated/graphql';

import { useAwardWalletBadgeFetchDetail } from './useAwardWalletBadgeFetchDetail';
import { useAwardWalletBadgeFetchList } from './useAwardWalletBadgeFetchList';

export const useAwardWalletBadgesDataSource = () => {
  const queryParams = ref<UseAwardWalletBadgeFetchListParams>({
    page: 1,
    first: 20,
    filters: {
      hasPublishedCollection: true,
    },
  });

  const allBadges = ref<Badge[]>([]);
  const loadedIds = new Set<string>();

  const {
    items,
    paginatorInfo,
    error,
    isFetching,
    isStale,
    refetch,
  } = useAwardWalletBadgeFetchList(queryParams);

  const loading = computed(() => isFetching.value || isStale.value)

  watch(items, (newItems) => {
    if (newItems.length === 0) {
      return;
    }

    const uniqueItems = newItems.filter(item => !loadedIds.has(item.id));
    uniqueItems.forEach((item) => {
      loadedIds.add(item.id)
    });

    allBadges.value = [...allBadges.value, ...uniqueItems];
  }, { immediate: true, deep: true });

  const loadMore = async () => {
    if (!paginatorInfo.value?.lastPage || queryParams.value.page >= paginatorInfo.value.lastPage) {
      return;
    }
    queryParams.value.page++;
    await refetch();
  }

  const retry = () => refetch({ requestPolicy: 'network-only' });

  return {
    queryParams,
    badges: allBadges,
    paginatorInfo,
    error,
    loading,
    loadMore,
    retry,
  };
};

export const useAwardWalletPremiumBadgesDataSource = () => {
  const { items, paginatorInfo, error, isFetching, isStale, refetch } = useAwardWalletBadgeFetchList(computed(() => ({
    page: 1,
    first: 20,
    filters: {
      tiers: [BadgeTier.Premium],
      hasPublishedCollection: true,
    },
  })));

  const loading = computed(() => isFetching.value || isStale.value)

  const retry = () => refetch({ requestPolicy: 'network-only' });

  return {
    badges: items,
    paginatorInfo,
    error,
    loading,
    retry,
  };
};

interface UseAwardWalletBadgeDetailDataSourceParams {
  slug: string;
}

export const useAwardWalletBadgeDetailDataSource = (params: Ref<UseAwardWalletBadgeDetailDataSourceParams>) => {
  const { items, paginatorInfo, error, isFetching, isStale } = useAwardWalletBadgeFetchDetail(params);

  const loading = computed(() => isFetching.value || isStale.value)

  const badge = computed(() => items.value[0] || null)

  return {
    badge,
    paginatorInfo,
    error,
    loading,
  };
};
